/**
 * 홈 「오늘」 (v2.319.0 · Salesforce 홈 Assistant · Today's Tasks/Events · 기능·메뉴 검토 B1)
 *
 *   네 칸: ① 내 할 일(기한 지남 → 오늘 → 7일 안 · 체크하면 완료 · 한 줄 추가) ② 오늘 납품예정(전체)
 *          ③ 주의 필요(내 영업건: 납품예정일 지남 · 2주 넘게 안 움직임 · 계산서 미발행 — Assistant 순서) ④ 내 승인 대기
 *   자료: GET /dashboard/today(할 일 · 내 영업건 요약 · 오늘 납품) + 결재함 pending-count(사이드바 배지와 같은 질의).
 *   영업 = 내 사번 건 · 경영지원·관리자 = 전체 · 조회 전용 = 오늘 납품예정만.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import * as approvalApi from '../lib/approval-api';
import { useAuth } from '../lib/auth-context';
import { createTask, patchTask, dueLabel, taskRelatedPath, type Task } from '../lib/tasks-api';
import { LoadingLines } from './ui/Skeleton';

interface MineSummary {
  openCount: number; openAmount: number;
  weekDeliveries: { total: number };
  overdue: { total: number; rows: { salesId: string; endUser: string | null; projectName: string | null; date?: string | null; days?: number | null }[] };
  stale: { total: number; rows: { salesId: string; endUser: string | null; projectName: string | null; days?: number | null }[] };
  unbilled: { total: number; maxDays: number; amount: number };
}
type TodayTask = Pick<Task, 'id' | 'title' | 'dueDate' | 'priority' | 'status' | 'relatedKind' | 'relatedId' | 'relatedLabel' | 'assigneeName'>;
interface Today {
  today: string;
  scope: 'mine' | 'all' | 'none';
  tasks: { overdue: TodayTask[]; today: TodayTask[]; upcoming: TodayTask[]; noDate: TodayTask[] };
  mine: MineSummary | null;
  deliveriesToday: { salesId: string; endUser: string | null; projectName: string | null; startTime: string | null; place: string | null; empName: string | null }[];
}

function won(n: number): string { return `${Math.round(n).toLocaleString('ko-KR')}원`; }

export function TodaySection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isViewer = user?.role === 'viewer';
  const q = useQuery({ queryKey: ['dashboard', 'today'], queryFn: () => api.get<Today>('/dashboard/today'), staleTime: 30_000, refetchInterval: 5 * 60_000 });
  const pendingQ = useQuery({ queryKey: ['approvals', 'pendingCount'], queryFn: approvalApi.getPendingCounts, enabled: !!user && !isViewer, staleTime: 60_000 });
  const invalidate = (): void => { void qc.invalidateQueries({ queryKey: ['dashboard', 'today'] }); void qc.invalidateQueries({ queryKey: ['tasks'] }); void qc.invalidateQueries({ queryKey: ['nav', 'bell'] }); };
  const done = useMutation({ mutationFn: (id: number) => patchTask(id, { status: '완료' }), onSuccess: invalidate, onError: (e: Error) => alert(`완료 처리 실패: ${e.message}`) });
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const add = useMutation({ mutationFn: () => createTask({ title: title.trim(), dueDate: due || null }), onSuccess: () => { setTitle(''); setDue(''); invalidate(); }, onError: (e: Error) => alert(`할 일 추가 실패: ${e.message}`) });

  const d = q.data;
  const pending = pendingQ.data?.count ?? 0;
  const taskRows: { group: string; tone: string; rows: TodayTask[] }[] = d ? [
    { group: '기한 지남', tone: 'text-red-600', rows: d.tasks.overdue },
    { group: '오늘', tone: 'text-amber-700', rows: d.tasks.today },
    { group: '7일 안', tone: 'text-slate-600', rows: d.tasks.upcoming },
    { group: '기한 없음', tone: 'text-slate-500', rows: d.tasks.noDate },
  ].filter((g) => g.rows.length > 0) : [];
  const taskTotal = d ? d.tasks.overdue.length + d.tasks.today.length + d.tasks.upcoming.length + d.tasks.noDate.length : 0;

  const TaskLine = ({ t }: { t: TodayTask }) => {
    const path = taskRelatedPath(t);
    return (
      <li className="flex items-center gap-2 text-sm">
        <input type="checkbox" id={`today-task-${t.id}`} checked={false} disabled={done.isPending} onChange={() => done.mutate(t.id)} title="완료로 표시" />
        <span className="flex-1 min-w-0 truncate" title={t.title}>{t.priority === '높음' && <span className="text-red-600 mr-1">❗</span>}{t.title}
          {t.relatedLabel && (path ? <Link to={path} className="ml-1 text-xs text-blue-700 hover:underline">{t.relatedLabel}</Link> : <span className="ml-1 text-xs text-slate-500">{t.relatedLabel}</span>)}
        </span>
        {t.dueDate && <span className="text-xs text-slate-500 whitespace-nowrap">{dueLabel(t.dueDate)}</span>}
      </li>
    );
  };

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-4 md:p-5 mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold">오늘 <span className="text-xs text-slate-500 font-normal ml-1">{d?.today ?? ''} · 할 일 · 납품 · 주의 거리 · 결재</span></div>
        {!isViewer && <Link to="/tasks" className="text-xs text-blue-700 hover:underline">할 일 전체 →</Link>}
      </div>
      {q.isLoading && <LoadingLines />}
      {q.isError && <div className="text-sm text-red-600">불러오기 실패: {(q.error as Error).message}</div>}
      {d && (
        <div className={`grid grid-cols-1 ${isViewer ? 'md:grid-cols-2' : 'md:grid-cols-2 xl:grid-cols-4'} gap-4`}>
          {/* ① 내 할 일 */}
          {!isViewer && (
            <div className="min-w-0">
              <div className="text-xs text-slate-500 mb-1.5">내 할 일 <b className={d.tasks.overdue.length ? 'text-red-600' : 'text-slate-700'}>{taskTotal}</b>{d.tasks.overdue.length > 0 && <span className="text-red-600"> · 기한 지남 {d.tasks.overdue.length}</span>}</div>
              {taskTotal === 0 && <div className="text-sm text-slate-400 mb-2">할 일 없음</div>}
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {taskRows.map((g) => (
                  <div key={g.group}>
                    <div className={`text-[11px] font-semibold ${g.tone}`}>{g.group} {g.rows.length}</div>
                    <ul className="space-y-1">{g.rows.slice(0, 8).map((t) => <TaskLine key={t.id} t={t} />)}</ul>
                    {g.rows.length > 8 && <Link to="/tasks" className="text-xs text-blue-700 hover:underline">… 외 {g.rows.length - 8}건</Link>}
                  </div>
                ))}
              </div>
              <form className="flex items-center gap-1.5 mt-2" onSubmit={(e) => { e.preventDefault(); if (title.trim()) add.mutate(); }}>
                <input id="today-task-title" className="ui-input ui-input-sm flex-1 min-w-0" placeholder="+ 할 일" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
                <input id="today-task-due" type="date" className="ui-input ui-input-sm w-[128px]" value={due} onChange={(e) => setDue(e.target.value)} title="기한" />
                <button type="submit" className="btn btn-secondary btn-sm" disabled={!title.trim() || add.isPending}>추가</button>
              </form>
            </div>
          )}

          {/* ② 오늘 납품예정 */}
          <div className="min-w-0">
            <div className="text-xs text-slate-500 mb-1.5">오늘 납품예정 <b className="text-slate-700">{d.deliveriesToday.length}</b></div>
            {d.deliveriesToday.length === 0 && <div className="text-sm text-slate-400">오늘 납품예정 없음</div>}
            <ul className="space-y-1 max-h-56 overflow-y-auto pr-1">
              {d.deliveriesToday.map((r) => (
                <li key={r.salesId} className="text-sm flex items-start gap-2">
                  <span className="text-xs text-slate-500 whitespace-nowrap w-12">{r.startTime ? r.startTime.slice(0, 5) : '종일'}</span>
                  <Link to={`/sales?open=${encodeURIComponent(r.salesId)}`} className="min-w-0 flex-1 truncate text-blue-700 hover:underline" title={[r.endUser, r.projectName, r.place].filter(Boolean).join(' · ')}>
                    {[r.endUser, r.projectName].filter(Boolean).join(' · ') || r.salesId}
                  </Link>
                  {r.empName && <span className="text-xs text-slate-500 whitespace-nowrap">{r.empName}</span>}
                </li>
              ))}
            </ul>
          </div>

          {/* ③ 주의 필요 */}
          {d.mine && (
            <div className="min-w-0">
              <div className="text-xs text-slate-500 mb-1.5">주의 필요 <span className="text-slate-400">({d.scope === 'mine' ? '내 영업건' : '전체 영업건'})</span></div>
              <ul className="space-y-1.5 text-sm">
                <li className="flex items-center justify-between gap-2">
                  <button type="button" className="text-left hover:underline" onClick={() => navigate('/sales')}>납품예정일 지난 건</button>
                  <b className={d.mine.overdue.total ? 'text-red-600' : 'text-slate-500'}>{d.mine.overdue.total}</b>
                </li>
                {d.mine.overdue.rows.slice(0, 3).map((r) => (
                  <li key={r.salesId} className="text-xs text-slate-600 truncate pl-2">· <Link to={`/sales?open=${encodeURIComponent(r.salesId)}`} className="hover:underline">{[r.endUser, r.projectName].filter(Boolean).join(' · ')}</Link>{r.days != null && <span className="text-red-600"> {r.days}일</span>}</li>
                ))}
                <li className="flex items-center justify-between gap-2">
                  <button type="button" className="text-left hover:underline" onClick={() => navigate('/sales')}>2주 넘게 안 움직인 건 <span className="text-xs text-slate-500">(일반·유력)</span></button>
                  <b className={d.mine.stale.total ? 'text-amber-700' : 'text-slate-500'}>{d.mine.stale.total}</b>
                </li>
                {d.mine.stale.rows.slice(0, 3).map((r) => (
                  <li key={r.salesId} className="text-xs text-slate-600 truncate pl-2">· <Link to={`/sales?open=${encodeURIComponent(r.salesId)}`} className="hover:underline">{[r.endUser, r.projectName].filter(Boolean).join(' · ')}</Link>{r.days != null && <span className="text-amber-700"> {r.days}일</span>}</li>
                ))}
                <li className="flex items-center justify-between gap-2">
                  <button type="button" className="text-left hover:underline" onClick={() => navigate('/sales')}>계산서 미발행</button>
                  <b className={d.mine.unbilled.total ? 'text-blue-700' : 'text-slate-500'}>{d.mine.unbilled.total}{d.mine.unbilled.total > 0 && <span className="text-xs font-normal text-slate-500"> · {won(d.mine.unbilled.amount)} · 최장 {d.mine.unbilled.maxDays}일</span>}</b>
                </li>
                <li className="text-xs text-slate-500 pt-1 border-t border-slate-100">진행 중 {d.mine.openCount}건 · 제안가 {won(d.mine.openAmount)} · 이번 주 납품예정 {d.mine.weekDeliveries.total}건</li>
              </ul>
            </div>
          )}

          {/* ④ 내 승인 대기 */}
          {!isViewer && (
            <div className="min-w-0">
              <div className="text-xs text-slate-500 mb-1.5">내 승인 대기</div>
              <Link to="/approvals" className="block rounded border border-slate-200 bg-slate-50 px-3 py-2 hover:bg-slate-100">
                <div className={`text-2xl font-bold ${pending ? 'text-rose-600' : 'text-slate-700'}`}>{pendingQ.isLoading ? '…' : `${pending}건`}</div>
                <div className="text-xs text-slate-500">판매보고서 · 사내결재 — 내가 승인할 차례{pendingQ.data ? ` (판보 ${pendingQ.data.salesReport ?? 0} · 사내 ${pendingQ.data.expense ?? 0})` : ''}</div>
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
